/**
 * Composed Create, the wizard's one automatic route after concept analysis:
 * plan → the plan's steps → spec synthesis → the acknowledgement → build. The
 * steps a plan runs (`composedPlannedStepsV1`) and the template they name
 * (`composedStepsTemplateIdV1`) are the server's, from @djgg/creations; this
 * file only says how each step is shown. The wizard runs them
 * (NewGameWizard) and never asks the creator to confirm between them.
 */
import type { ComposePlanV1, ComposedPlannedStepV1 } from '@djgg/creations';
import { composedFitCandidatesV1 } from '@djgg/creations';
import { plainPlanText } from './creatorCopy.js';

/** The progress lines of the stages every composed run shares, in the creator's terms. */
export const COMPOSED_STAGE_LABELS = {
  plan: 'Planning how your game is built',
  synthesis: 'Fitting your idea onto the game and proving it is winnable',
  build: 'Starting the build',
} as const;

/** The first step of a revision: the live game it changes. */
const COMPOSED_BASE_STEP_LABEL = 'Starting from your current game';

/** A step's progress line, in the creator's terms. */
export function composedCreateStepLabelV1(step: ComposedPlannedStepV1): string {
  switch (step.kind) {
    case 'base': return COMPOSED_BASE_STEP_LABEL;
    case 'template': return 'Composing a new game from the platform’s proven parts';
    case 'adaptation': return 'Adapting the template to your idea';
    case 'rule': return `Writing a new rule: ${plainPlanText(step.need.need)}`;
  }
}

/** The plan's base template by its catalog title (null for a new game). */
export function composedPlanTemplateTitleV1(plan: ComposePlanV1 | null): string | null {
  if (!plan?.templateId) return null;
  return composedFitCandidatesV1().find(candidate => candidate.templateId === plan.templateId)?.label ?? null;
}
